
import { useState } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import { animated, useTransition } from 'react-spring';
import styled from 'styled-components';
import { NavbarProps } from './query';
import Button_v2 from '@/components/molecules/Button/index';
import Link from '@/components/molecules/Link';
import { Svg } from '@/components/styled';
import BRUCE_LOGO_RED_JSX from '@/public/img/bruce-logo/bruce_logo_red.jsx';
import BRUCE_LOGO_RED_PNG from '@/public/img/bruce-logo/bruce_logo_red.png';
import { List as IconsList } from '@/public/img/icons';
import devices from '@/utilities/devices';
import { renderIcons } from '@/utilities/functions';

// #region STYLE
const NavbarContainer = styled.nav`
	background-color: ${({theme}) => theme.color.white};
	border-bottom: 2px solid ${({theme}) => theme.color.greyLight};
	display: flex;
	font-size: ${({theme}) => theme.fontSize.DEFAULT};
	justify-content: space-between;
	padding: 1.4rem 1.5rem;
	position: sticky;
	top: 0;
	z-index: 999;

	.left-container {
		align-items: center;
		display: flex;
		span:nth-child(2) {
			margin: 0 2.8rem;
		}
		.logo {
			display: flex;
		}
		.navlinks {
			color: ${({theme}) => theme.color.greyDarker};
			text-decoration: none;
			transition: .3s;

			:hover {
				color: ${({theme}) => theme.color.black};
			}
		}
	}

	.right-container {
		.button {
			background-color: ${({theme}) => theme.color.white};
			border: 1px solid ${({theme}) => theme.color.grey};
			border-radius: 30px;
			color: ${({theme}) => theme.color.black};
			font-size: ${({theme}) => theme.fontSize.XSMALL};
			font-weight: bold;
			padding: .65rem 1.2rem;
			text-decoration: none;
			text-transform: uppercase;
			transition: .3s;

			:hover {
				background-color: ${({theme}) => theme.color.greyLight};
			}
		}
	}
	@media screen and (${devices.mobile}) {
		display: none;
	}
	@media screen and (${devices.minTablet}) and (${devices.maxTablet}) {
		display: none;
	}
`;

const MobileNavbarContainer = styled.nav`
	display: none;
	background-color: ${({theme}) => theme.color.white};
	border-bottom: 2px solid ${({theme}) => theme.color.greyLight};
	font-size: ${({theme}) => theme.fontSize.DEFAULT};
	justify-content: space-between;
	padding: .8rem 1rem;
	position: sticky;
	top: 0;
	z-index: 999;
	.logo {
		transform: translateY(5px);
	}
	@media screen and (${devices.mobile}) {
		display: flex;
	}
	@media screen and (${devices.minTablet}) and (${devices.maxTablet}) {
		display: flex;
	}
`;
const BurgerButton = styled.div<{ isOpen: boolean }>`
	cursor: pointer;
	display: none;
	position: relative;
	z-index: 1000;
	div {
		transform: ${({isOpen}) => isOpen && 'translateX(10px)'};
		transition: .3s;
		:after {
			background-color: ${({theme}) => theme.color.black};
			border-radius: 10px;
			content: '';
			display: block;
			height: 2px;
			margin: 7px 0;
			transition: .3s;
			width: 25px;
		}

		:first-child:after {
			background-color: ${({isOpen, theme}) => isOpen && theme.color.black};
			transform: ${({isOpen}) => isOpen ? 'rotate(45deg) translateY(13px) ' : 'rotate(0deg)'};
		}

		:nth-child(2):after {
			background-color: ${({isOpen, theme}) => isOpen && theme.color.black};
			opacity: ${({isOpen}) => isOpen ? 0 : 1};
			width: ${({isOpen}) => isOpen ? '0px' : '25px' };
		}

		:last-child:after {
			background-color: ${({isOpen, theme}) => isOpen && theme.color.black};
			transform: ${({isOpen}) => isOpen ? 'rotate(-45deg) translateY(-13px)' : 'rotate(0deg)'};
		}
	}
	@media screen and (${devices.mobile}) {
		display: block;
	}
	@media screen and (${devices.minTablet}) and (${devices.maxTablet}) {
		display: block;
	}
`;
const bottomContainerHeight = '74px';
const MobileOverlay = styled.div<{ isVisible: boolean }>`
	background-color: ${({theme}) => theme.color.white};
	display: none;
	// https://stackoverflow.com/questions/37112218/css3-100vh-not-constant-in-mobile-browser
	/* height: calc(100vh - calc(100vh - 100%)); */
	height: calc(100% - 62px);
	position: fixed;
	transform: ${({isVisible}) => isVisible ? 'translateX(0vw)' : 'translateX(100vw)'};
	transition: transform .35s ease;
	width: 100vw;
	will-change: transform;
	z-index: 1000;

	.content-container {
		box-sizing: border-box;
		/* height: calc(100% - (62px + ${bottomContainerHeight})); */
		height: calc(100% - (${bottomContainerHeight}));
		overflow-y: auto;
		opacity: ${({isVisible}) => isVisible ? 1 : 0};
		transition: .5s ease .15s;
	}

	.bottom-container {
		display: flex;
		align-items: flex-start;
		background-color: ${({theme}) => theme.color.white};
		box-shadow: 0px 0px 20px rgba(63, 6, 43, 0.09);
		box-sizing: border-box;
		overflow: auto;
		overflow-y: hidden;
		gap: .5rem;
		padding: 0 .5rem;
		height: ${bottomContainerHeight};
	}

	@media screen and (${devices.mobile}) {
		display: block;
	}
	@media screen and (${devices.minTablet}) and (${devices.maxTablet}) {
		display: block;
	}
`;

const MobileBackgroundOverlay = styled(animated.div)`
	display: block;
	position: fixed;
	width: 100%;
	height: 100%;
	left: 0;
	top: 0;
	background-color: black;
	z-index: 999;
`;
// #endregion

const Navbar_v2 = ({ navbarData }: { navbarData: NavbarProps }) => {

	const [isMobileOverlayOpen, setIsMobileOverlayOpen] = useState(false);
	const springTransition = useTransition(isMobileOverlayOpen, {
		from: { opacity: 0 },
		enter: { opacity: .6 },
		leave: { opacity: 0 },
		config: {
			friction: 40,
			tension: 210,
		},
	});

	const OpenMobileOverlay = () => {
		setIsMobileOverlayOpen(!isMobileOverlayOpen);
		isMobileOverlayOpen ? (
			document.body.style.overflow = 'auto',
			document.body.style.overflowX = 'hidden'
		) : (
			document.body.style.overflow = 'hidden'
		);
	};

	return (
		<>
			<NavbarContainer>
				<div className='left-container'>
					<Link href='/' className='logo'>
						<BRUCE_LOGO_RED_JSX />
					</Link>
					{navbarData.links.map(
						(link) => (
							<Link
								className='navlinks'
								href={link.url}
								key={link.url}
								openOnNewTab={false}
								text={link.text}
							/>
						),
					)}
				</div>
				<div className='right-container'>
					{navbarData.buttons.map(
						(button) => (
							<Link
								className='button'
								href={button.url}
								key={button.url}
								openOnNewTab={false}
								text={button.text}
							/>
						),
					)}
				</div>
			</NavbarContainer>

			{springTransition((style, item) => (
				item && <MobileBackgroundOverlay style={style}/>
			))}
			<MobileNavbarContainer>
				<Link href='/' className='logo'>
					<img
						alt='bruce logo'
						src={BRUCE_LOGO_RED_PNG.src}
					/>
				</Link>
				<BurgerButton
					isOpen={isMobileOverlayOpen}
					onClick={() => OpenMobileOverlay()}
				>
					<div />
					<div />
					<div />
				</BurgerButton>
			</MobileNavbarContainer>

			<MobileOverlay isVisible={isMobileOverlayOpen}>
				<div className='content-container'>
					<MobileNavAccordion linksMobile={navbarData.linksMobile}  />
				</div>
				<div className='bottom-container'>
					{navbarData.buttonsMobile.map((button, index) => (
						<Button_v2
							gtm_id={button.trackerId}
							href={button.url}
							key={index}
							openOnNewTab={button.newTab}
							text={button.text}
							version={button.variant}
						/>
					))}
				</div>
			</MobileOverlay>
		</>
	);
};

export default Navbar_v2;

// #region MOBILE NAV ACCORDION
const LinkContainer = styled.div<{ isOpen: boolean | undefined }>`
	border-bottom: ${({ theme }) => `1px solid ${theme.color.greyLight}`};
	padding: calc(1.3rem - 5px) 1.625rem;
	cursor: pointer;

	a {
		display: block;
		color: ${({ theme }) => theme.color.greyDarker};
		text-decoration: none;
		user-select: none;
		-webkit-tap-highlight-color: transparent;
  		-webkit-touch-callout: none;
		-moz-tap-highlight-color: transparent;
  		-moz-touch-callout: none;
	}

	.title-container {
		display: flex;
		align-items: center;
		justify-content: flex-start;
		box-sizing: content-box;

		svg {
			width: 22px;
			height: 22px;
			padding-top: 5px;
		}

		.title {
			padding: 0 1.25rem;
			flex: 1 1 0;
			:first-letter {
				text-transform: capitalize;
			}
		}

		.chevron {
			transition: .2s;
			padding-top: 2px;
			transform: ${({ isOpen }) => isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
		}
	}

	.sublink-container {
		display: flex;
		flex-direction: column;
		max-height: ${({ isOpen }) => isOpen ? '30vh' : '0vh'};
		opacity: ${({ isOpen }) => isOpen ? 1 : 0};
		overflow: auto;
		padding-top: .5rem;
		transition: max-height .4s, opacity .3s;
		scrollbar-width: none;
		::-webkit-scrollbar {
    		display: none;
		}

		a {
			display: block;
			color: ${({ theme }) => theme.color.greyDarker};
			padding: .4rem 1rem;
			text-decoration: none;
			:first-letter {
				text-transform: capitalize;
			}
		}
	}
`;

const MobileNavAccordion = ({ linksMobile }: { linksMobile: NavbarProps['linksMobile'] }) => {

	const [isAccordionOpen, setIsAccordionOpen] = useState<{ [index: string]: boolean }>({});

	return (
		<>
			{linksMobile.map((navElement) => (
				<LinkContainer
					key={navElement.id}
					onClick={() => setIsAccordionOpen(prevState => ({
						[navElement.id]: !prevState[navElement.id],
					}))}
					isOpen={isAccordionOpen[navElement.id]}
				>
					<Link href={navElement?.url}>
						<div className='title-container'>
							<Svg path='prune'> {renderIcons(navElement.icon.name, IconsList)} </Svg>
							<div className='title'> {navElement.text} </div>
							{(navElement.subLinks && navElement.subLinks.length > 0) && (
								<div className='chevron'> <FiChevronDown /> </div>
							)}
						</div>
						{(navElement?.subLinks && navElement?.subLinks?.length > 0) && (
							<div className='sublink-container'>
								{navElement.subLinks.map(
									(subLink, key) => (
										<Link
											key={key}
											href={subLink.url}
											text={subLink.text}
										/>
									),
								)}
							</div>
						)}
					</Link>
				</LinkContainer>
			))}
		</>
	);
};
// #endregion
